import {
  useCallback,
  useEffect,
  useState } from 'react';
import {
  AlertIcon,
  Badge,
  Empty,
  Grid,
  Note,
  Panel,
  RefreshIcon,
  ShieldIcon,
  Spinner,
  Stat,
  TableWrap,
  shortId,
} from '@wyocspay/ui';
import { badgeLabel, dateTime, money, moneyHeadline, relative, statusLabel } from './format.js';
import { StatusEditor } from './StatusEditor.js';
import { ApiError, api, type AdminOverview, type AuditPage, type Device, type Merchant } from './api.js';
import {
  buildCommand,
  generateKeyPair,
  requireIdentity,
  saveIdentity,
  signCommand,
  type DeviceIdentity,
} from './device.js';

function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  // Live: refresh in the background every few seconds so statuses, transactions
  // and every other list update without reloading the page. Silent (no spinner),
  // and paused while the tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void run(true);
    }, 5000);
    return () => clearInterval(id);
  }, [run]);

  return { data, error, loading, reload: run };
}

function Frame({ loading, error, children }: { loading: boolean; error: string | null; children: React.ReactNode }) {
  if (loading) return <Spinner label="Зареждане…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  return <>{children}</>;
}

// ---------------------------------------------------------------------------

export function OverviewView() {
  const { data, error, loading } = useLoad<AdminOverview>(() => api.overview());

  return (
    <Frame loading={loading} error={error}>
      {data ? (
        <div style={{ display: 'grid', gap: 18 }}>
          {(data.open_alerts.critical ?? 0) > 0 ? (
            <Note tone="danger" title={`Отворени критични сигнали: ${data.open_alerts.critical}`}>
              Критичен сигнал значи, че реконсилиацията е открила нещо, което няма да реши сама -
              разминаване в сума, валута или свързан профил, или промяна на състоянието, която нищо
              легитимно не може да е причинило. Отидете в <strong>Сигнали</strong>.
            </Note>
          ) : null}

          <Grid cols={4}>
            <Stat
              label="Оборот · 30 дни"
              value={moneyHeadline(data.volume_30d).value}
              hint={moneyHeadline(data.volume_30d).hint ?? undefined}
              accent="var(--wc-green)"
            />
            <Stat
              label="Приходи на платформата · 30 дни"
              value={money(data.platform_revenue_30d.total, data.platform_revenue_30d.currency)}
              hint={
                data.platform_revenue_30d.estimated > 0
                  ? `от които ${money(data.platform_revenue_30d.estimated, data.platform_revenue_30d.currency)} преизчислени`
                  : 'Такси към платформата'
              }
            />
            <Stat label="Плащания · 30 дни" value={data.payments_30d} accent="var(--wc-amber)" />
            <Stat
              label="Търговци"
              value={Object.values(data.merchants).reduce((a, b) => a + b, 0)}
              hint={Object.entries(data.merchants).map(([k, v]) => `${v} ${statusLabel(k, v)}`).join(' · ')}
              accent="var(--wc-grey)"
            />
          </Grid>

          {data.platform_revenue_30d.estimated > 0 || data.platform_revenue_30d.missingRates.length > 0 ? (
            <Note tone="warn" title="Част от приходите не са в долари">
              {data.platform_revenue_30d.estimated > 0 ? (
                <div style={{ fontSize: 13.5 }}>
                  {money(data.platform_revenue_30d.exact, 'usd')} са реално получени в долари.
                  Останалите{' '}
                  {money(data.platform_revenue_30d.estimated, 'usd')} са преизчислени по вашите
                  референтни курсове -{' '}
                  {data.platform_revenue_30d.applied
                    .map((a) => `${money(a.amount, a.currency)} @ ${a.rate}`)
                    .join(', ')}
                  . Stripe конвертира по свой курс при изплащане, така че тази част е приблизителна.
                </div>
              ) : null}
              {data.platform_revenue_30d.missingRates.length > 0 ? (
                <div style={{ fontSize: 13.5, marginTop: 6 }}>
                  Без зададен курс и затова <strong>извън</strong> сумата по-горе:{' '}
                  {data.platform_revenue_30d.missingRates.map((c) => c.toUpperCase()).join(', ')}.
                  Задайте ги в Настройки на платформата.
                </div>
              ) : null}
            </Note>
          ) : null}

          <Grid cols={2}>
            <Panel title="Реконсилиация">
              {data.last_reconciliation ? (
                <dl style={{ margin: 0, display: 'grid', gap: 10 }}>
                  <KV k="Последно изпълнение" v={relative(data.last_reconciliation.started_at)} />
                  <KV k="Проверени плащания" v={String(data.last_reconciliation.checked)} />
                  <KV
                    k="Несъответствия"
                    v={
                      <Badge tone={data.last_reconciliation.discrepancies > 0 ? 'warn' : 'ok'}>
                        {data.last_reconciliation.discrepancies}
                      </Badge>
                    }
                  />
                  <KV k="Автоматично коригирано от Stripe" v={String(data.last_reconciliation.auto_corrected)} />
                  <KV
                    k="Ескалирани"
                    v={
                      <Badge tone={data.last_reconciliation.escalated > 0 ? 'fail' : 'ok'}>
                        {data.last_reconciliation.escalated}
                      </Badge>
                    }
                  />
                </dl>
              ) : (
                <Empty>Реконсилиацията още не е изпълнявана. Нужни са ключове за Stripe.</Empty>
              )}
            </Panel>

            <Panel title="Опашка за доставка">
              <dl style={{ margin: 0, display: 'grid', gap: 10 }}>
                <KV k="Чакащи webhooks към търговци" v={String(data.pending_webhook_deliveries)} />
                <KV
                  k="Отворени сигнали"
                  v={
                    Object.keys(data.open_alerts).length === 0
                      ? 'няма'
                      : Object.entries(data.open_alerts)
                          .map(([k, v]) => `${v} ${statusLabel(k, v)}`)
                          .join(' · ')
                  }
                />
              </dl>
            </Panel>
          </Grid>
        </div>
      ) : null}
    </Frame>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
      <dt style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{k}</dt>
      <dd style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--wc-ink)' }}>{v}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function MerchantsView({
  identity,
  actorId,
  onOpen,
}: {
  identity: DeviceIdentity | null;
  actorId: string;
  onOpen: (id: string) => void;
}) {
  const { data, error, loading, reload } = useLoad<{ data: Merchant[] }>(() => api.merchants());
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  /**
   * Every state change is a signed command. The signature is produced by the
   * device's non-exportable key, carries the version the row was read at, and
   * uses a nonce the server will accept exactly once.
   */
  async function act(merchant: Merchant, transition: string, reason: string): Promise<void> {
    setBusy(merchant.id);
    setResult(null);
    try {
      const signer = requireIdentity(identity);
      const cmd = buildCommand({
        actorId,
        deviceId: signer.deviceId,
        target: `merchant:${merchant.id}`,
        transition,
        previousVersion: merchant.version,
        params: { reason },
      });
      const signature = await signCommand(signer, cmd);
      await api.command(cmd, signature);
      setResult({ tone: 'ok', text: `${merchant.name}: ${transition} - приложено и записано.` });
      await reload();
    } catch (err) {
      setResult({
        tone: 'danger',
        text: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Промените тук са подписани команди">
        Спирането или възстановяването на търговец не е обновяване в базата. Това е команда, подписана от това устройство, обвързана с версията, която гледате, валидна еднократно. Отхвърлените опити също се записват.
      </Note>

      {result ? <Note tone={result.tone}>{result.text}</Note> : null}

      <Frame loading={loading} error={error}>
        <Panel padded={false}>
          {data && data.data.length > 0 ? (
            <TableWrap>
              <table className="wc-table">
                <thead>
                  <tr>
                    <th>Търговец</th>
                    <th>Статус</th>
                    <th>Stripe</th>
                    <th>Плащания</th>
                    <th>Такса</th>
                    <th>Вер.</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <strong>{m.name}</strong>
                        <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{m.email}</div>
                      </td>
                      <td>
                        <Badge
                          tone={
                            m.status === 'ACTIVE' ? 'ok'
                            : m.status === 'PENDING' ? 'pending'
                            : m.status === 'SUSPENDED' ? 'fail'
                            : 'neutral'
                          }
                        >
                          {badgeLabel(m.status)}
                        </Badge>
                      </td>
                      <td style={{ fontSize: 12.5 }}>
                        {m.stripe_account_id ? (
                          <>
                            <span className="wc-num">{shortId(m.stripe_account_id, 5)}</span>
                            <div>
                              <Badge tone={m.charges_enabled ? 'ok' : 'pending'} icon={false}>
                                {m.charges_enabled ? 'ПРИЕМА ПЛАЩАНИЯ' : 'НЕ ПРИЕМА'}
                              </Badge>
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--wc-faint)' }}>няма профил</span>
                        )}
                      </td>
                      <td className="wc-num">{m.payments}</td>
                      <td className="wc-num" style={{ fontSize: 12.5 }}>
                        {(m.fee.percent_bps / 100).toFixed(2)}% + {(m.fee.fixed / 100).toFixed(2)}
                        {/*
                          * Отбелязано в списъка, а не само на страницата на
                          * търговеца: „кои клиенти не ми плащат такса“ е въпрос,
                          * на който се отговаря с поглед, а „0.00% + 0.00“ се
                          * чете като липсваща стойност също толкова лесно,
                          * колкото и като решение.
                          */}
                        {m.fee.percent_bps === 0 && m.fee.fixed === 0 ? (
                          <div>
                            <Badge tone="info" icon={false}>ОСВОБОДЕН</Badge>
                          </div>
                        ) : null}
                      </td>
                      <td className="wc-num" style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>{m.version}</td>
                      <td className="wc-right" style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="wc-btn wc-btn-sm wc-btn-secondary"
                          onClick={() => onOpen(m.id)}
                        >
                          Отвори
                        </button>{' '}
                        {m.status === 'SUSPENDED' ? (
                          <button
                            type="button"
                            className="wc-btn wc-btn-sm"
                            disabled={busy === m.id}
                            onClick={() => act(m, 'reinstate', 'прегледано и изчистено')}
                          >
                            Възстанови
                          </button>
                        ) : m.status !== 'CLOSED' ? (
                          <button
                            type="button"
                            className="wc-btn wc-btn-sm wc-btn-danger"
                            disabled={busy === m.id}
                            onClick={() => act(m, 'suspend', 'преглед на риска')}
                          >
                            Спри
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Empty>Няма търговци.</Empty>
          )}
        </Panel>
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function TransactionsView({ onOpen }: { onOpen: (id: string) => void }) {
  const [state, setState] = useState('all');
  const { data, error, loading, reload } = useLoad(() => api.transactions(state), [state]);
  const states = ['all', 'SUCCEEDED', 'PROCESSING', 'FAILED', 'REFUNDED', 'DISPUTED'];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {states.map((s) => (
          <button
            key={s}
            type="button"
            className={`wc-btn wc-btn-sm ${state === s ? '' : 'wc-btn-secondary'}`}
            onClick={() => setState(s)}
          >
            {s === 'all' ? 'Всички' : badgeLabel(s)}
          </button>
        ))}
      </div>

      <Frame loading={loading} error={error}>
        <Panel padded={false}>
          {data && data.data.length > 0 ? (
            <TableWrap>
              <table className="wc-table">
                <thead>
                  <tr><th>Плащане</th><th>Търговец</th><th>Статус</th><th>Създаден</th><th className="wc-right">Такса</th><th className="wc-right">Сума</th></tr>
                </thead>
                <tbody>
                  {data.data.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => onOpen(p.id)}
                      style={{ cursor: 'pointer' }}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p.id); }}
                    >
                      <td className="wc-num" style={{ fontSize: 12.5 }}>{shortId(p.id)}</td>
                      <td>{p.merchant.name}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <StatusEditor paymentId={p.id} state={p.status} onChanged={() => void reload(true)} />
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{relative(p.created_at)}</td>
                      <td className="wc-right wc-num" style={{ fontSize: 13, color: 'var(--wc-muted)' }}>
                        {money(p.application_fee, p.currency)}
                      </td>
                      <td className="wc-right wc-num" style={{ fontWeight: 600 }}>{money(p.amount, p.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Empty>Няма транзакции.</Empty>
          )}
        </Panel>
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function AlertsView({ identity, actorId }: { identity: DeviceIdentity | null; actorId: string }) {
  const { data, error, loading, reload } = useLoad(() => api.alerts('OPEN'));
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function resolve(id: string): Promise<void> {
    setBusy(id);
    setMsg(null);
    try {
      const signer = requireIdentity(identity);
      const cmd = buildCommand({
        actorId,
        deviceId: signer.deviceId,
        target: `alert:${id}`,
        transition: 'resolve',
        previousVersion: 0,
        params: { note: 'прегледано в администрацията' },
      });
      await api.command(cmd, await signCommand(signer, cmd));
      await reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Frame loading={loading} error={error}>
      {msg ? <div style={{ marginBottom: 14 }}><Note tone="danger">{msg}</Note></div> : null}
      <Panel padded={false}>
        {data && data.data.length > 0 ? (
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>Тежест</th><th>Вид</th><th>Обект</th><th>Детайли</th><th>Подаден</th><th /></tr></thead>
              <tbody>
                {data.data.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Badge tone={a.severity === 'critical' ? 'fail' : a.severity === 'warning' ? 'warn' : 'info'}>
                        {a.severity.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="wc-num" style={{ fontSize: 12.5 }}>{a.kind}</td>
                    <td className="wc-num" style={{ fontSize: 12 }}>{shortId(a.subject, 5)}</td>
                    <td style={{ fontSize: 13, maxWidth: 420 }}>{a.detail}</td>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)', whiteSpace: 'nowrap' }}>{relative(a.created_at)}</td>
                    <td className="wc-right">
                      <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy === a.id} onClick={() => resolve(a.id)}>
                        Разреши
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        ) : (
          <Empty>Няма отворени сигнали.</Empty>
        )}
      </Panel>
    </Frame>
  );
}

// ---------------------------------------------------------------------------

export function DevicesView({
  identity,
  actorId,
  onEnrolled,
}: {
  identity: DeviceIdentity | null;
  actorId: string;
  onEnrolled?: () => void | Promise<void>;
}) {
  const { data, error, loading, reload } = useLoad<{ data: Device[] }>(() => api.devices());
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function revoke(device: Device): Promise<void> {
    setBusy(device.id);
    setMsg(null);
    try {
      const signer = requireIdentity(identity);
      const cmd = buildCommand({
        actorId,
        deviceId: signer.deviceId,
        target: `device:${device.id}`,
        transition: 'revoke',
        previousVersion: 0,
        params: { reason: 'отнето от администрацията' },
      });
      const res = await api.command(cmd, await signCommand(signer, cmd));
      setMsg(`${device.name} е отнето. Незабавно прекратени сесии: ${res.new_version}.`);
      await reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Give this browser a signing key.
   *
   * A client certificate opens the console; it does not sign control commands.
   * A browser that has never had a device key can therefore read everything and
   * change nothing, and this is how that is fixed without a one-time code.
   *
   * Nothing is loosened by it. The key pair is generated here with the private
   * half non-exportable, exactly as the old enrolment did, and it is registered
   * through `POST /admin/devices` - the endpoint that has always let a
   * signed-in Super Administrator register a public key, under the same
   * permission it has always required. What has changed is only which
   * credential got the person to this page.
   */
  async function enrolThisBrowser(): Promise<void> {
    setBusy('enrol');
    setMsg(null);
    try {
      const pair = await generateKeyPair();
      const ua = navigator.userAgent;
      const registered = await api.registerDevice({
        user_id: actorId,
        name: ua.includes('Firefox') ? 'Firefox'
          : ua.includes('Chrome') ? 'Chrome'
          : ua.includes('Safari') ? 'Safari'
          : 'Browser',
        device_type: 'browser',
        os: navigator.platform,
        public_key_pem: pair.publicKeyPem,
      });
      await saveIdentity({
        deviceId: registered.device_id,
        userId: actorId,
        privateKey: pair.privateKey,
        publicKeyPem: pair.publicKeyPem,
      });
      setMsg('Този браузър вече има ключ за подписване. Привилегированите команди са възможни.');
      await onEnrolled?.();
      await reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Отнемането е незабавно и пълно">
        Отнемането на устройство унищожава съхранения му ключ, спира всеки бъдещ подпис и прекратява активните му сесии в същата операция. В одитната верига се записва събитие с тежест HIGH.
      </Note>

      {identity ? null : (
        <Note tone="warn" title="Този браузър няма ключ за подписване">
          Сертификатът ви дава достъп до администрацията, но всяка команда, която променя състояние - спиране на търговец, рефунд, промяна на настройка - се подписва с ключ на устройството. Ключът се създава тук и частният му ключ никога не напуска този браузър.
          <div style={{ marginTop: 10 }}>
            <button type="button" className="wc-btn wc-btn-sm" disabled={busy === 'enrol'} onClick={enrolThisBrowser}>
              <ShieldIcon size={13} /> {busy === 'enrol' ? 'Създаване…' : 'Създай ключ за този браузър'}
            </button>
          </div>
        </Note>
      )}

      {msg ? <Note tone="warn">{msg}</Note> : null}

      <Frame loading={loading} error={error}>
        <Panel padded={false}>
          {data && data.data.length > 0 ? (
            <TableWrap>
              <table className="wc-table">
                <thead><tr><th>Устройство</th><th>Потребител</th><th>Ключ</th><th>Регистрирано</th><th>Последно видяно</th><th>Статус</th><th /></tr></thead>
                <tbody>
                  {data.data.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <strong>{d.name}</strong>
                        <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
                          {d.device_type}{d.os ? ` · ${d.os}` : ''}
                          {identity && d.id === identity.deviceId ? <Badge tone="info" icon={false}>ТОВА УСТРОЙСТВО</Badge> : null}
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>{d.user.name}<div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>{badgeLabel(d.user.role)}</div></td>
                      <td className="wc-num" style={{ fontSize: 11.5 }}>{d.key_id.slice(0, 20)}…</td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{dateTime(d.registered_at)}</td>
                      <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{d.last_seen_at ? relative(d.last_seen_at) : 'никога'}</td>
                      <td>
                        <Badge tone={d.status === 'ACTIVE' ? 'ok' : 'fail'}>{badgeLabel(d.status)}</Badge>
                        {d.revoked_reason ? <div style={{ fontSize: 11.5, color: 'var(--wc-muted)' }}>{d.revoked_reason}</div> : null}
                      </td>
                      <td className="wc-right">
                        {d.status === 'ACTIVE' && d.id !== identity?.deviceId ? (
                          <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" disabled={busy === d.id} onClick={() => revoke(d)}>
                            Отнеми
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Empty>Няма регистрирани устройства.</Empty>
          )}
        </Panel>
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function AuditView() {
  const { data, error, loading, reload } = useLoad<AuditPage>(() => api.audit());

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Frame loading={loading} error={error}>
        {data ? (
          <>
            {data.chain_valid ? (
              <Note tone="ok" title="Одитната верига е проверена">
                Всичките {data.chain_entries} записа се хешират наново коректно и се свързват с предходния.
                Нищо не е редактирано или изтрито.
              </Note>
            ) : (
              <Note tone="danger" title="Одитната верига е нарушена">
                Проверката се провали на запис {data.chain_broken_at} ({data.chain_break_reason}).
                Историята е променена извън приложението. Третирайте всеки запис след тази точка като
                недостоверен и проверете достъпа до базата незабавно.
              </Note>
            )}

            <Panel
              title="Одитен дневник"
              padded={false}
              actions={
                <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => void reload()}>
                  <RefreshIcon size={13} /> Провери отново
                </button>
              }
            >
              <TableWrap>
                <table className="wc-table">
                  <thead><tr><th>#</th><th>Кога</th><th>Действие</th><th>Извършител</th><th>Обект</th><th>Хеш</th></tr></thead>
                  <tbody>
                    {data.data.map((e) => (
                      <tr key={e.sequence}>
                        <td className="wc-num" style={{ color: 'var(--wc-faint)' }}>{e.sequence}</td>
                        <td style={{ fontSize: 12.5, color: 'var(--wc-muted)', whiteSpace: 'nowrap' }}>{dateTime(e.at)}</td>
                        <td><strong style={{ fontSize: 13.5 }}>{e.action}</strong></td>
                        <td><Badge tone={e.actor === 'stripe_webhook' ? 'ok' : e.actor === 'reconciler' ? 'info' : 'neutral'}>{e.actor}</Badge></td>
                        <td className="wc-num" style={{ fontSize: 11.5 }}>{shortId(e.subject, 5)}</td>
                        <td className="wc-num" style={{ fontSize: 11, color: 'var(--wc-faint)' }}>{e.hash.slice(0, 12)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            </Panel>
          </>
        ) : null}
      </Frame>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function SystemView() {
  const { data, error, loading, reload } = useLoad(() => api.system());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function runReconcile(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.reconcile();
      setMsg(
        `Проверени плащания: ${r.checked}. Несъответствия: ${r.discrepancies}, от които ${r.autoCorrected} приети от Stripe и ${r.escalated} ескалирани.`,
      );
      await reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {msg ? <Note>{msg}</Note> : null}

      <Frame loading={loading} error={error}>
        {data ? (
          <>
            <Grid cols={3}>
              <Stat
                label="Събития от Stripe"
                value={data.stripe_events.total}
                hint={`${data.stripe_events.failed} неуспешни`}
                accent={data.stripe_events.failed > 0 ? 'var(--wc-red)' : 'var(--wc-green)'}
              />
              <Stat
                label="Webhooks към търговци"
                value={data.merchant_webhooks.total}
                hint={`${data.merchant_webhooks.exhausted} изчерпани`}
                accent={data.merchant_webhooks.exhausted > 0 ? 'var(--wc-amber)' : 'var(--wc-green)'}
              />
              <Stat
                label="Одитна верига"
                value={data.audit_chain.valid ? 'VALID' : 'BROKEN'}
                hint={`${data.audit_chain.entries} записа`}
                accent={data.audit_chain.valid ? 'var(--wc-green)' : 'var(--wc-red)'}
              />
            </Grid>

            <Panel
              title="Изпълнения на реконсилиация"
              padded={false}
              actions={
                <button type="button" className="wc-btn wc-btn-sm" onClick={runReconcile} disabled={busy}>
                  <ShieldIcon size={13} /> {busy ? 'Running…' : 'Стартирай сега'}
                </button>
              }
            >
              {data.reconciliation_runs.length > 0 ? (
                <TableWrap>
                  <table className="wc-table">
                    <thead><tr><th>Започнат</th><th>Статус</th><th>Проверени</th><th>Несъответствия</th><th>Ескалирани</th></tr></thead>
                    <tbody>
                      {data.reconciliation_runs.map((r) => (
                        <tr key={r.id}>
                          <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{dateTime(r.started_at)}</td>
                          <td><Badge tone={r.status === 'COMPLETE' ? 'ok' : r.status === 'FAILED' ? 'fail' : 'pending'}>{badgeLabel(r.status)}</Badge></td>
                          <td className="wc-num">{r.checked}</td>
                          <td className="wc-num">{r.discrepancies}</td>
                          <td className="wc-num">
                            {r.escalated > 0 ? <span style={{ color: 'var(--wc-red)', fontWeight: 600 }}>{r.escalated}</span> : 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              ) : (
                <Empty>
                  <AlertIcon size={14} /> Реконсилиацията не е изпълнявана. Изисква ключове за Stripe.
                </Empty>
              )}
            </Panel>
          </>
        ) : null}
      </Frame>
    </div>
  );
}
